import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, Upload } from "lucide-react";

const FIELDS = [
  { key: "hero_title", label: "Hero title", type: "text", group: "Hero" },
  { key: "hero_subtitle", label: "Hero subtitle", type: "textarea", group: "Hero" },
  { key: "hero_tagline", label: "Hero tagline (small label above title)", type: "text", group: "Hero" },
  { key: "about_title", label: "About section title", type: "text", group: "About" },
  { key: "about_body", label: "About story", type: "textarea", rows: 6, group: "About" },
  { key: "artisan_name", label: "Your artisan/brand name", type: "text", group: "About" },
  { key: "contact_email", label: "Contact email", type: "email", group: "Contact" },
  { key: "contact_phone", label: "Contact phone", type: "text", group: "Contact" },
  { key: "contact_location", label: "Studio location", type: "text", group: "Contact" },
  { key: "social_instagram_url", label: "Instagram URL (your profile, e.g. https://instagram.com/auburndruid)", type: "text", group: "Connect / Social" },
  { key: "social_facebook_url", label: "Facebook URL", type: "text", group: "Connect / Social" },
  { key: "social_youtube_url", label: "YouTube channel URL", type: "text", group: "Connect / Social" },
  { key: "social_soundcloud_url", label: "SoundCloud URL", type: "text", group: "Connect / Social" },
  { key: "featured_reel_url", label: "Featured Instagram reel URL to embed (e.g. https://www.instagram.com/reels/XXXX/) — leave blank for no embed", type: "text", group: "Connect / Social" },
];

const MAX_BYTES = 8 * 1024 * 1024;

export default function AdminContent() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const aboutImgRef = useRef(null);
  const fluteImgRef = useRef(null);
  const drumImgRef = useRef(null);
  const didjImgRef = useRef(null);

  useEffect(() => {
    api.get("/site-content").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <p className="text-[var(--text-secondary)]">Loading...</p>;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { setStatus({ ok: false, msg: "Image too large (max 8MB)." }); return; }
    const r = new FileReader();
    r.onload = () => update("about_image", r.result);
    r.readAsDataURL(file);
  };

  const onImageFile = (field, file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { setStatus({ ok: false, msg: "Image too large (max 8MB)." }); return; }
    const r = new FileReader();
    r.onload = () => update(field, r.result);
    r.readAsDataURL(file);
  };

  const onSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const { data: saved } = await api.put("/admin/site-content", data);
      setData(saved);
      setStatus({ ok: true, msg: "Saved. Your public site is updated." });
    } catch (e) {
      setStatus({ ok: false, msg: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-content-page">
      <p className="label-eyebrow mb-2">Studio · Edit</p>
      <h1 className="font-display text-4xl mb-2">Site content</h1>
      <p className="text-[var(--text-secondary)] text-sm mb-8">
        Edit the text people see on your home page. Changes go live the moment you save.
      </p>

      <div className="admin-card p-6 space-y-8">
        {["Hero", "About", "Contact", "Connect / Social"].map((group) => (
          <div key={group}>
            <p className="font-display text-2xl mb-4 pb-2 border-b border-[var(--si-border)]">{group}</p>
            <div className="space-y-5">
              {FIELDS.filter((f) => f.group === group).map((f) => (
                <div key={f.key}>
                  <label className="label-eyebrow block mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      className="input-field"
                      rows={f.rows || 3}
                      value={data[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                    />
                  ) : (
                    <input
                      type={f.type}
                      className="input-field"
                      value={data[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div>
          <label className="label-eyebrow block mb-2">About section image (your workshop)</label>
          <div className="flex gap-4 items-start">
            {data.about_image ? (
              <img src={data.about_image} alt="" className="w-32 h-32 object-cover rounded-md border border-[var(--border)]" />
            ) : (
              <div className="w-32 h-32 rounded-md bg-[var(--bg-secondary)] border border-dashed border-[var(--border)] flex items-center justify-center text-xs text-[var(--text-secondary)]">Default</div>
            )}
            <div className="flex-1 space-y-2">
              <input type="file" accept="image/*" hidden ref={aboutImgRef} onChange={(e) => onFile(e.target.files?.[0])} data-testid="about-image-file" />
              <button type="button" className="btn-outline text-sm py-2 px-4" onClick={() => aboutImgRef.current?.click()} data-testid="about-image-upload">
                <Upload size={14} /> Upload
              </button>
              <input
                className="input-field text-xs"
                placeholder="...or paste image URL"
                value={data.about_image && data.about_image.startsWith("data:") ? "" : (data.about_image || "")}
                onChange={(e) => update("about_image", e.target.value)}
                data-testid="about-image-url"
              />
              {data.about_image && (
                <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => update("about_image", "")}>Clear image</button>
              )}
            </div>
          </div>
        </div>

        {/* Home page category showcase images */}
        <div>
          <p className="font-display text-2xl mb-2 pb-2 border-b border-[var(--si-border)]">Home page · category images</p>
          <p className="text-sm text-[var(--si-text-2)] mb-5">
            Pick the image that represents each category on your Home page (the three big cards: Love Flutes, Drums, Didgeridoos). If you leave one blank, the site will pick the first instrument photo from that category automatically.
          </p>
          {[
            { key: "home_flute_image", label: "Pharaoh Love Flutes image", ref: fluteImgRef, testId: "flute" },
            { key: "home_drum_image", label: "Drums image", ref: drumImgRef, testId: "drum" },
            { key: "home_didgeridoo_image", label: "Didgeridoos image", ref: didjImgRef, testId: "didj" },
          ].map((row) => (
            <div key={row.key} className="mb-6">
              <label className="label-eyebrow block mb-2">{row.label}</label>
              <div className="flex gap-4 items-start">
                {data[row.key] ? (
                  <img src={data[row.key]} alt="" className="w-32 h-32 object-cover rounded-md border border-[var(--si-border)]" />
                ) : (
                  <div className="w-32 h-32 rounded-md bg-[var(--si-bg-2)] border border-dashed border-[var(--si-border)] flex items-center justify-center text-xs text-[var(--si-text-2)] text-center px-2">Auto from gallery</div>
                )}
                <div className="flex-1 space-y-2">
                  <input type="file" accept="image/*" hidden ref={row.ref} onChange={(e) => onImageFile(row.key, e.target.files?.[0])} data-testid={`home-${row.testId}-image-file`} />
                  <button type="button" className="btn-outline text-sm py-2 px-4" onClick={() => row.ref.current?.click()} data-testid={`home-${row.testId}-image-upload`}>
                    <Upload size={14} /> Upload
                  </button>
                  <input
                    className="input-field text-xs"
                    placeholder="...or paste image URL"
                    value={data[row.key] && data[row.key].startsWith("data:") ? "" : (data[row.key] || "")}
                    onChange={(e) => update(row.key, e.target.value)}
                    data-testid={`home-${row.testId}-image-url`}
                  />
                  {data[row.key] && (
                    <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => update(row.key, "")}>Clear (auto-pick instead)</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button onClick={onSave} disabled={saving} className="btn-primary" data-testid="content-save">
          <Save size={16} /> {saving ? "Saving..." : "Save changes"}
        </button>
        {status && (
          <p className={`text-sm ${status.ok ? "text-[var(--secondary-accent)]" : "text-red-700"}`} data-testid="content-status">
            {status.msg}
          </p>
        )}
      </div>
    </div>
  );
}
